import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { appLocaleMessages, availableLocales } from '@/i18n'

export type SupportedLocale = 'vi' | 'en' | 'zh'

interface TranslationOptions {
  /** Giá trị trả về khi thiếu key (thay vì trả về chính key). */
  _?: string | number
  [key: string]: string | number | undefined
}

interface I18nContextValue {
  locale: SupportedLocale
  setLocale: (locale: SupportedLocale) => void
  t: (key: string, options?: TranslationOptions) => string
  common: (key: string, options?: TranslationOptions) => string
  resource: (resource: string, key: string, options?: TranslationOptions) => string
  component: (component: string, key: string, options?: TranslationOptions) => string
  auth: (key: string, options?: TranslationOptions) => string
  navigation: (key: string, options?: TranslationOptions) => string
  availableLocales: typeof availableLocales
}

const STORAGE_KEY = 'azl_locale'

function getValue(obj: Record<string, unknown>, keyPath: string): unknown {
  const keys = keyPath.split('.')
  let value: unknown = obj
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = (value as Record<string, unknown>)[k]
    } else {
      return undefined
    }
  }
  return value
}

/** Đọc `appLocaleMessages` mỗi lần — tránh HMR giữ bản locale cũ (hiện `resources.*.fields.*`). */
function translate(locale: SupportedLocale, key: string, options?: TranslationOptions): string {
  const messages = appLocaleMessages as Record<SupportedLocale, Record<string, unknown>>
  let value = getValue(messages[locale], key)
  if (typeof value !== 'string' && locale !== 'vi') {
    value = getValue(messages.vi, key)
  }
  if (typeof value !== 'string') return options?._ !== undefined ? String(options._) : key
  if (!options) return value
  return value.replace(/\{(\w+)\}/g, (_match, variable) => String(options[variable] ?? _match))
}

/**
 * Thiếu key thì `resource(ns, key)` trả về `resources.<ns>.<key>` (không phải `key`),
 * nên so sánh `label === key` không bắt được — dùng helper này khi chỉ có wrapper `(k) => string`.
 */
export const isMissingTranslation = (label: string, key: string): boolean =>
  label === key || label.endsWith(`.${key}`)

const I18nContext = createContext<I18nContextValue | null>(null)

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}

const getInitialLocale = (): SupportedLocale => {
  if (typeof window === 'undefined') return 'vi'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'vi' || stored === 'en' || stored === 'zh') return stored
  return 'vi'
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<SupportedLocale>(getInitialLocale)

  const setLocale = useCallback((l: SupportedLocale) => {
    setLocaleState(l)
    localStorage.setItem(STORAGE_KEY, l)
  }, [])

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, options) => translate(locale, key, options),
      common: (key, options) => translate(locale, `common.${key}`, options),
      resource: (res, key, options) => translate(locale, `resources.${res}.${key}`, options),
      component: (comp, key, options) => translate(locale, `components.${comp}.${key}`, options),
      auth: (key, options) => translate(locale, `auth.${key}`, options),
      navigation: (key, options) => translate(locale, `navigation.${key}`, options),
      availableLocales,
    }),
    [locale, setLocale]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
